import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAgentStore } from '@/stores/agentStore';
import { useCanvasEvents } from '@/hooks/useCanvasEvents';
import { useCollaborationWebSocket } from '@/hooks/useCollaborationWebSocket';
import { MessageInput } from './components/MessageInput';
import { useChatHistory } from './hooks/useChatHistory';
import { useAssistantActions } from './hooks/useAssistantActions';
import { useAgentStream } from './hooks/useAgentStream';
import { useAttachmentManager } from './hooks/useAttachmentManager';
import { useResizablePanel } from './hooks/useResizablePanel';
import { AgentStatusBar } from './components/AgentStatusBar';
import { MessageList } from './components/MessageList';
import { DegradationBanner } from './DegradationBanner';
import { BackgroundTaskCard } from './BackgroundTaskCard';
import { ClarificationInput } from './ClarificationInput';

interface AssistantPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AssistantPanel: React.FC<AssistantPanelProps> = ({ isOpen, onClose }) => {
  // P1-1: 移除高频 store 字段订阅（streamingText/isThinking/activeProcessLogs/activeAssistantIntro）
  // 这些字段仅用于滚动到底部的 useEffect，改用 store.subscribe 独立订阅
  const {
    messages, isLoading,
    agentSessionId,
    degradationLevel, degradationMessage,
    backgroundTasks,
  } = useAgentStore(useShallow((s) => ({
    messages: s.messages,
    isLoading: s.isLoading,
    agentSessionId: s.sessionId,
    degradationLevel: s.degradationLevel,
    degradationMessage: s.degradationMessage,
    backgroundTasks: s.backgroundTasks,
  })));

  const [clarificationData, setClarificationData] = useState<any>(null);

  // P0-1: 使用 registerPanel 直接操作 DOM，拖拽期间不触发 React 重渲染
  const { handleResizeStart, registerPanel } = useResizablePanel(420);
  const { attachments, fileInputRef, handleFileSelect, removeAttachment } = useAttachmentManager();

  const { sendClarificationReply } = useCollaborationWebSocket({
    sessionId: agentSessionId || '',
    autoConnect: !!agentSessionId,
    onClarificationRequest: (data) => {
      setClarificationData(data);
    },
  });

  const panelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { applyWorkflow } = useCanvasEvents();

  const {
    refreshChatHistory,
    handleNewChat,
    handleLoadHistory,
    handleDeleteHistory,
  } = useChatHistory();

  const {
    handleStopGeneration,
    handleGenerationConfirm,
    handleGenerationCancel,
    handleSendMessage,
    refreshChatHistoryRef,
  } = useAssistantActions();

  refreshChatHistoryRef.current = refreshChatHistory;

  useAgentStream();

  useEffect(() => {
    const { loadAgents, agents } = useAgentStore.getState();
    if (agents.length === 0) {
      loadAgents();
    }
  }, []);

  // 页面隐藏时保存智能体聊天状态
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        useAgentStore.getState().saveToLocal();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // P1-1: 改用 store.subscribe 独立订阅滚动触发，避免高频字段触发组件重渲染
  useEffect(() => {
    let prevLen = useAgentStore.getState().messages.length;
    let prevThinking = useAgentStore.getState().isThinking;
    let prevStreaming = useAgentStore.getState().streamingText;
    const unsub = useAgentStore.subscribe((state) => {
      const curLen = state.messages.length;
      const curThinking = state.isThinking;
      const curStreaming = state.streamingText;
      if (curLen !== prevLen || curThinking !== prevThinking || curStreaming !== prevStreaming) {
        prevLen = curLen;
        prevThinking = curThinking;
        prevStreaming = curStreaming;
        if (isOpen && scrollContainerRef.current) {
          const el = scrollContainerRef.current;
          requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
        }
      }
    });
    return unsub;
  }, [isOpen]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) e.preventDefault();
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (useAgentStore.getState().showHistoryModal && panelRef.current) {
        const target = e.target as Node;
        const dropdown = panelRef.current.querySelector('[data-history-dropdown]');
        const historyBtn = panelRef.current.querySelector('[data-history-btn]');
        if (dropdown && !dropdown.contains(target) && historyBtn && !historyBtn.contains(target)) {
          useAgentStore.getState().setShowHistoryModal(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    if (isOpen) {
      panelRef.current?.addEventListener('wheel', handleWheel, { passive: false });
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      panelRef.current?.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen]);

  // P1-2: 使用 useCallback 包裹传给 MessageList 的回调，避免 memo 失效
  const handleSkillClick = useCallback((skillLabel: string) => {
    useAgentStore.getState().setInput(skillLabel);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // P0-2: 仅过渡 transform 和 opacity，不包含 width，避免拖拽时过渡冲突
  const SPRING_ANIMATION = "transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]";

  return (
    <div
      ref={(el) => { panelRef.current = el; registerPanel(el); }}
      className={`fixed right-0 top-0 bottom-0 bg-[#18181a] z-40 flex flex-col ${SPRING_ANIMATION} ${isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'}`}
      style={{ borderTopLeftRadius: '16px', borderBottomLeftRadius: '16px' }}
      onWheel={(e) => {
        e.stopPropagation();
        if (e.ctrlKey) e.preventDefault();
      }}
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-cyan-500/50 transition-colors z-50"
        onMouseDown={handleResizeStart}
        title="拖拽调整宽度"
      />

      <AgentStatusBar
        onNewChat={handleNewChat}
        onClose={onClose}
        onLoadHistory={handleLoadHistory}
        onDeleteHistory={handleDeleteHistory}
      />

      {degradationLevel > 0 && (
        <DegradationBanner level={degradationLevel} message={degradationMessage} />
      )}

      <div ref={scrollContainerRef} className="flex-1 px-5 custom-scrollbar flex flex-col min-h-0">
        <MessageList
          onSkillClick={handleSkillClick}
          onGenerationConfirm={handleGenerationConfirm}
          onGenerationCancel={handleGenerationCancel}
          onApplyWorkflow={applyWorkflow}
        />

        {backgroundTasks && backgroundTasks.length > 0 && (
          <div className="mt-2 space-y-2">
            {backgroundTasks.map((task: any) => (
              <BackgroundTaskCard key={task.taskId || task.call_id} task={task} />
            ))}
          </div>
        )}

        {clarificationData && (
          <ClarificationInput
            callId={clarificationData.callId}
            question={clarificationData.question}
            options={clarificationData.options}
            onSubmit={(callId: string, reply: string) => {
              sendClarificationReply(callId, reply);
              setClarificationData(null);
            }}
            onCancel={() => setClarificationData(null)}
          />
        )}
        <div />
      </div>

      <MessageInput
        attachments={attachments}
        textareaRef={textareaRef}
        fileInputRef={fileInputRef}
        onSend={handleSendMessage}
        onStop={handleStopGeneration}
        onFileSelect={handleFileSelect}
        onRemoveAttachment={removeAttachment}
      />
    </div>
  );
};
